import type { RuleCondition } from "@sauron/contracts"
import { CostBand, coversCountry, DEFERRED_PAYMENT_QUESTION_ID } from "@sauron/contracts"
import type { ConfigField, Registry } from "@/api.js"

// The engine's own coverage test, re-exported so `Settings.tsx` shares the one
// answer to "does this source serve this country" rather than a second copy.
export { coversCountry }

/**
 * The document the panel edits, flattened into something a drag-and-drop
 * editor can hold: one list of rules per country **and question**, each rule
 * knowing whether it is on, in what order, at what weight, and in which mode.
 */
export interface EditorRule {
  readonly ruleId: string
  readonly title: string
  readonly description: string
  readonly dependsOn: readonly string[]
  /** The codes this rule can write. Shown in its detail, so a ticket can name one. */
  readonly reasonCodes: readonly string[]
  /** What this rule declared about itself, so the panel renders its controls. */
  readonly fields: readonly ConfigField[]
  enabled: boolean
  weight: number
  mode: "veto" | "score"
  onUnavailable: "veto" | "points" | "ignore"
  /** The rule's own settings. Reaches `evaluate` as `RuleInput.params`. */
  params: Record<string, unknown>
  /**
   * When this rule runs at all. Empty — the usual case — means always.
   *
   * **Held here and written back on publish**, because publishing rewrites
   * every rule entry from this model: a field the editor does not carry is a
   * field the next publish deletes. That is not hypothetical — it is what
   * happened to `params` before they were round-tripped.
   */
  conditions: RuleCondition[]
}

/** One question's rule set in one country, and the line it is measured against. */
export interface EditorQuestion {
  readonly questionId: string
  /** Polish, from the registry — what the strip shows. */
  readonly title: string
  blockThreshold: number
  rules: EditorRule[]
}

/**
 * Rule sets nest under the country, per question. Which countries exist is a
 * property of the whole system (Ustawienia); which questions a country's sets
 * answer is what this build answers, always — a country never lacks a set for
 * a question, it can only have one with nothing switched on.
 */
export interface EditorCountry {
  readonly code: string
  questions: EditorQuestion[]
}

/** Every rule switched on across a country's questions. */
export function enabledCount(country: EditorCountry): number {
  return country.questions.reduce(
    (total, set) => total + set.rules.filter((rule) => rule.enabled).length,
    0,
  )
}

/**
 * Priority encodes the **cost** of a rule's provider, not anyone's sense of
 * importance — short-circuiting only saves anything when the expensive work is
 * last. So dragging sets the order *within* a band, and the band itself comes
 * from what the providers declare. A rule cannot be dragged out of costing what
 * it costs.
 */
export function bandOf(rule: EditorRule, registry: Registry): number {
  const costs = rule.dependsOn.map((providerId) => {
    const provider = registry.providers.find((candidate) => candidate.providerId === providerId)
    return provider?.worstCaseCost ?? CostBand.LOCAL
  })
  return costs.length === 0 ? CostBand.LOCAL : Math.max(...costs)
}

export interface Lane {
  /** The band floor, and the base every priority in this lane is offset from. */
  readonly band: number
  readonly title: string
  readonly hint: string
}

/**
 * The three lanes rules run in, cheapest first.
 *
 * **They are the execution order made visible.** The editor was one flat list
 * that could be dragged into any order, while `assignPriorities` re-sorted by
 * cost band underneath — so the screen could show an order the engine would not
 * run. Lanes make the invariant physical: a rule is drawn in the lane its
 * source costs, and it cannot be dragged out of it.
 */
export const LANES: readonly Lane[] = [
  { band: CostBand.LOCAL, title: "Lokalne", hint: "dane u nas, pytane pierwsze" },
  { band: CostBand.UNMETERED, title: "Bez limitu", hint: "wywołanie sieciowe bez rachunku" },
  {
    band: CostBand.METERED,
    title: "Limitowane",
    hint: "wydaje limit albo pieniądze, pytane na końcu",
  },
]

/** The lane a cost falls in: its band floor. Nothing says a source declares one. */
export function laneOfCost(cost: number): number {
  if (cost >= CostBand.METERED) return CostBand.METERED
  if (cost >= CostBand.UNMETERED) return CostBand.UNMETERED
  return CostBand.LOCAL
}

/** Which lane a rule is drawn in: the floor of its worst-case cost band. */
export function laneOf(rule: EditorRule, registry: Registry): number {
  return laneOfCost(bandOf(rule, registry))
}

/**
 * Where a rule's source stands among the installed ones — the order the
 * registry lists them in, which is the order the sources were integrated.
 *
 * **Inside a lane the rules run source by source, and this is what says which
 * source first.** The list is grouped by source because that is how a person
 * looks for a rule, and a grouping that is only drawn is one the order can
 * drift out of: a rule merged into a published document — `gus-business-age`,
 * added after the last publish — landed at the end of its lane, behind KRZ and
 * VIES, as a group of one with no handle to drag it back by (*nie umiem tego
 * chwycić*, 2026-09-15). So the source order is part of the order the editor
 * holds, a rule reading two sources stands with the later of them, and what
 * dragging sets is the order within a source. The engine loses nothing:
 * every source in a lane is in flight before the first rule runs, so the
 * order between sources decides only which finding is written first.
 */
export function sourceRank(rule: EditorRule, registry: Registry): number {
  const ranks = rule.dependsOn.map((providerId) =>
    registry.providers.findIndex((provider) => provider.providerId === providerId),
  )
  return ranks.length === 0 ? -1 : Math.max(...ranks)
}

/** One vocabulary for cost: the rule grid and the source list say the same word. */
export function laneTitle(cost: number): string {
  return LANES.find((lane) => lane.band === laneOfCost(cost))?.title ?? "Lokalne"
}

/**
 * Position in the lane becomes priority, offset by the lane's floor.
 *
 * **Keyed on the lane rather than on the exact declared cost.** A source
 * declaring 150 sits in the same lane as one declaring 100, so counting them
 * separately would let the list show one order while the priorities said
 * another — the same lie the lanes exist to remove.
 */
export function assignPriorities(rules: readonly EditorRule[], registry: Registry): number[] {
  const seen = new Map<number, number>()
  return rules.map((rule) => {
    const lane = laneOf(rule, registry)
    const index = seen.get(lane) ?? 0
    seen.set(lane, index + 1)
    return lane + index
  })
}

/**
 * The rule list with the enabled rules put in a new order, leaving the disabled
 * ones where they sit.
 *
 * A drag reorders what runs; it says nothing about what does not. Rebuilding
 * the list as "reordered, then everything disabled" would quietly move every
 * switched-off rule to the bottom, so re-enabling one landed it somewhere
 * nobody put it.
 */
export function withEnabledOrder(
  rules: readonly EditorRule[],
  order: readonly EditorRule[],
): EditorRule[] {
  const queue = [...order]
  return rules.map((rule) => (rule.enabled ? (queue.shift() ?? rule) : rule))
}

/**
 * **The engine's own applicability test — the same `coversCountry`, not a
 * rewrite: a rule can run in a country
 * only when every provider it depends on covers it.**
 *
 * The panel has to apply it too, not merely trust the engine to. Offering the
 * MF VAT rule under DE let somebody configure, weigh and publish a rule the
 * engine drops on sight — a rule set that reads as protection and is not. A
 * rule depending on nothing is applicable everywhere, which is what `every` on
 * an empty list already says.
 */
export function appliesTo(
  dependsOn: readonly string[],
  registry: Registry,
  country: string,
): boolean {
  return dependsOn.every((providerId) => {
    const provider = registry.providers.find((candidate) => candidate.providerId === providerId)
    return provider !== undefined && coversCountry(provider, country)
  })
}

/**
 * A rule answers only the questions it declares — coverage, like a provider's
 * countries — so it is offered under a question only when it says it answers
 * it. The engine applies the same test when it merges and evaluates.
 */
export function answers(questions: readonly string[], questionId: string): boolean {
  return questions.includes(questionId)
}

export interface CountryCoverage {
  /** Countries the installed sources name outright. */
  readonly codes: readonly string[]
  /** True when some source covers every country, so any code is legitimate. */
  readonly anyCountry: boolean
}

/**
 * Which countries can be configured at all — the union of what the installed
 * sources serve. **A country nothing covers is not a rule set, it is an empty
 * one**, and the panel offering a free-text box is how `DE` came to exist with
 * a Polish VAT rule in it.
 */
export function configurableCountries(registry: Registry): CountryCoverage {
  const codes = new Set<string>()
  let anyCountry = false

  for (const provider of registry.providers) {
    if (provider.countries === "all") anyCountry = true
    else for (const code of provider.countries) codes.add(code)
  }

  return { codes: [...codes].sort(), anyCountry }
}

export interface EngineSettings {
  readonly blockThreshold: number
  readonly uncoveredCountryPolicy: string
}

/** What a provider is set to, by the keys its manifest declares. */
export type EditorProviders = Record<string, Record<string, unknown>>

/**
 * Provider settings as the engine reads them: every declared key present, on
 * its default where the document is silent.
 *
 * **Filled in rather than left absent**, for the reason the rule half is: the
 * diff compares normalised documents, so a document that simply omits a
 * setting must not read as a change the first time the panel writes down what
 * was already happening.
 *
 * A key the manifest does not declare is carried through untouched. Publishing
 * rebuilds the document from here, so anything this build cannot render is
 * something the next publish would otherwise delete — which is exactly what
 * happened to every rule's `params` once.
 */
export function providersOf(
  document: Record<string, unknown>,
  registry: Registry,
): EditorProviders {
  const stored: Record<string, unknown> = isRecord(document["providers"])
    ? document["providers"]
    : {}
  const providers: EditorProviders = {}

  for (const provider of registry.providers) {
    const raw = stored[provider.providerId]
    const settings: Record<string, unknown> = isRecord(raw) ? { ...raw } : {}
    for (const field of provider.fields) {
      if (settings[field.key] === undefined) settings[field.key] = field.default
    }
    providers[provider.providerId] = settings
  }

  // A provider this build does not have keeps its entry, like a rule the
  // document names and the build does not: the engine drops it on sight and
  // says so, and deleting it here would lose somebody's setting to a deploy
  // that removed a source for an afternoon.
  for (const [providerId, entry] of Object.entries(stored)) {
    if (providers[providerId] === undefined && isRecord(entry)) {
      providers[providerId] = { ...entry }
    }
  }

  return providers
}

/** The engine's own two settings, read out of a document with the same fallbacks. */
export function engineOf(document: Record<string, unknown>): EngineSettings {
  return {
    blockThreshold: numberOr(document["blockThreshold"], 100),
    uncoveredCountryPolicy: document["uncoveredCountryPolicy"] === "allow" ? "allow" : "prepayment",
  }
}

/**
 * A stored document as the engine actually reads it: every rule present, every
 * default filled in.
 *
 * **The diff beside the publish button compares normalised documents, not raw
 * JSON.** A stored document may simply omit a rule — the engine then runs it on
 * manifest defaults — so comparing literals would report a change every time
 * the panel wrote down what was already happening. Normalising both sides makes
 * the list of changes the list of edits somebody actually made.
 */
export function normalize(
  document: Record<string, unknown>,
  registry: Registry,
): Record<string, unknown> {
  const engine = engineOf(document)
  return toDocument(
    fromDocument(document, registry),
    engine.blockThreshold,
    engine.uncoveredCountryPolicy,
    registry,
    providersOf(document, registry),
  )
}

export function toDocument(
  countries: readonly EditorCountry[],
  engineThreshold: number,
  uncoveredCountryPolicy: string,
  registry: Registry,
  providers: EditorProviders = {},
): Record<string, unknown> {
  return {
    blockThreshold: engineThreshold,
    uncoveredCountryPolicy,
    providers,
    countries: Object.fromEntries(
      countries.map((country) => [
        country.code,
        {
          questions: Object.fromEntries(
            country.questions.map((set) => [set.questionId, setToDocument(set, registry)]),
          ),
        },
      ]),
    ),
  }
}

function setToDocument(set: EditorQuestion, registry: Registry): Record<string, unknown> {
  const enabled = set.rules.filter((rule) => rule.enabled)
  const priorities = assignPriorities(enabled, registry)

  return {
    blockThreshold: set.blockThreshold,
    rules: [
      ...enabled.map((rule, index) => ({
        ruleId: rule.ruleId,
        enabled: true,
        priority: priorities[index] ?? 0,
        weight: rule.weight,
        mode: rule.mode,
        onUnavailable: rule.onUnavailable,
        conditions: rule.conditions,
        params: rule.params,
      })),
      // Disabled rules are kept in the document rather than dropped, so the
      // panel can show them as available and turning one back on restores
      // what it was set to.
      ...set.rules
        .filter((rule) => !rule.enabled)
        .map((rule) => ({
          ruleId: rule.ruleId,
          enabled: false,
          priority: laneOf(rule, registry),
          weight: rule.weight,
          mode: rule.mode,
          onUnavailable: rule.onUnavailable,
          conditions: rule.conditions,
          params: rule.params,
        })),
    ],
  }
}

export function fromDocument(
  document: Record<string, unknown>,
  registry: Registry,
): EditorCountry[] {
  const engineThreshold = numberOr(document["blockThreshold"], 100)
  const stored = isRecord(document["countries"]) ? document["countries"] : {}
  const codes = Object.keys(stored).length === 0 ? ["PL"] : Object.keys(stored)

  // The country actually served comes first. A panel that opens on DE — an
  // empty set that answers prepayment to everything — reads like a fault.
  const ordered = [...codes].sort((a, b) => (a === "PL" ? -1 : b === "PL" ? 1 : a.localeCompare(b)))

  return ordered.map((code) => {
    const country = isRecord(stored[code]) ? (stored[code] as Record<string, unknown>) : {}
    const storedSets = storedQuestionsOf(country)

    return {
      code,
      // Every question this build answers gets a set, whether or not the
      // document names it — the engine merges an unmentioned question on
      // manifest defaults, and the panel has to show what will run.
      questions: registry.questions.map((question) =>
        setFromDocument(
          question,
          code,
          isRecord(storedSets[question.questionId])
            ? (storedSets[question.questionId] as Record<string, unknown>)
            : {},
          engineThreshold,
          registry,
        ),
      ),
    }
  })
}

/**
 * A document published before 2026-09-01 carried one rule set per country and
 * no question level. That set was the deferred-payment question's — the only
 * question there was — so it is read as such, exactly as the engine reads it.
 */
function storedQuestionsOf(country: Record<string, unknown>): Record<string, unknown> {
  if (isRecord(country["questions"])) return country["questions"]
  if (Array.isArray(country["rules"])) return { [DEFERRED_PAYMENT_QUESTION_ID]: country }
  return {}
}

function setFromDocument(
  question: Registry["questions"][number],
  code: string,
  set: Record<string, unknown>,
  engineThreshold: number,
  registry: Registry,
): EditorQuestion {
  const configured = Array.isArray(set["rules"]) ? set["rules"] : []

  const rules = registry.rules
    // A rule its sources cannot answer for in this country, or that does not
    // answer this question, is not offered: the engine drops it, so
    // configuring it would be configuring nothing.
    .filter(
      (manifest) =>
        appliesTo(manifest.dependsOn, registry, code) &&
        answers(manifest.questions, question.questionId),
    )
    .map((manifest) => {
      const entry = configured.find(
        (candidate): candidate is Record<string, unknown> =>
          isRecord(candidate) && candidate["ruleId"] === manifest.ruleId,
      )

      return {
        ruleId: manifest.ruleId,
        title: manifest.title,
        description: manifest.description,
        dependsOn: manifest.dependsOn,
        reasonCodes: manifest.reasonCodes ?? [],
        fields: manifest.fields,
        enabled: entry === undefined ? true : entry["enabled"] !== false,
        weight: numberOr(entry?.["weight"], 0),
        mode: readMode(entry?.["mode"], manifest.defaultMode),
        onUnavailable: readPolicy(entry?.["onUnavailable"], manifest.defaultOnUnavailable),
        params: readParams(entry?.["params"], manifest.fields),
        conditions: readConditions(entry?.["conditions"], manifest.defaultConditions),
      } satisfies EditorRule
    })

  // Sorted into lanes, then into sources inside a lane, so the array the
  // editor holds is already the order the engine runs after a publish: a drag
  // is then a plain move inside one source. What the document stored decides
  // the order within a source, and a rule it does not mention goes last there.
  const ordered = [...rules].sort(
    (a, b) =>
      laneOf(a, registry) - laneOf(b, registry) ||
      sourceRank(a, registry) - sourceRank(b, registry) ||
      orderOf(a, configured) - orderOf(b, configured),
  )
  return {
    questionId: question.questionId,
    title: question.title,
    blockThreshold: numberOr(set["blockThreshold"], engineThreshold),
    rules: ordered,
  }
}

/**
 * A rule's own settings, seeded from what it declared.
 *
 * **Keys this build does not declare are kept, not dropped.** They may belong
 * to a rule a newer build declares, and publishing is a full rewrite of the
 * document — so anything the panel cannot render, it must at least carry.
 * Before `params` were round-tripped at all, publishing from this screen
 * silently deleted every stored parameter.
 */
function readParams(stored: unknown, fields: readonly ConfigField[]): Record<string, unknown> {
  const params: Record<string, unknown> = isRecord(stored) ? { ...stored } : {}
  for (const field of fields) {
    if (params[field.key] === undefined) params[field.key] = field.default
  }
  return params
}

/**
 * A rule's conditions, seeded from what its manifest declares.
 *
 * **A stored list is taken as it stands, empty included.** Empty means somebody
 * cleared the conditions on purpose, and re-seeding the manifest's defaults over
 * that would put back what they removed on every reload. Only a document that
 * says nothing at all falls back to the declaration — the same reading the
 * engine's own parser applies.
 *
 * Conditions this build cannot render are carried through untouched for the
 * reason unknown `params` keys are: publishing is a full rewrite, so anything
 * the panel cannot show, it must at least not delete.
 */
function readConditions(
  stored: unknown,
  declared: readonly RuleCondition[] | undefined,
): RuleCondition[] {
  if (Array.isArray(stored)) return [...(stored as RuleCondition[])]
  return [...(declared ?? [])]
}

function orderOf(rule: EditorRule, configured: readonly unknown[]): number {
  const entry = configured.find(
    (candidate): candidate is Record<string, unknown> =>
      isRecord(candidate) && candidate["ruleId"] === rule.ruleId,
  )
  return numberOr(entry?.["priority"], Number.MAX_SAFE_INTEGER)
}

/**
 * The same fallback order the engine uses, and the same reason `readPolicy` has
 * one: a control opening on a different value than the decision path applies
 * makes the screen lie about what is running.
 *
 * **A stored mode is read literally, for every rule.** It used to fall through
 * to `defaultMode` whenever the document did not say `score`, which read a
 * stored `veto` back as whatever the rule shipped as; and until 2026-09-09 a
 * `canVeto: false` manifest demoted a stored `veto` outright, so the panel
 * refused a promotion the person editing had asked for. Both wrote a demotion
 * back to the document on the next publish. That is the one path `mode` exists
 * for: scoring first is how a rule earns the right to block, and promoting it
 * is somebody's decision to make.
 */
function readMode(value: unknown, fallback: "veto" | "score"): "veto" | "score" {
  if (value === "veto" || value === "score") return value
  return fallback
}

/**
 * The same fallback order the engine uses. The panel opening a control on a
 * different default than the one the decision path applies would make the
 * screen lie about what is running.
 */
function readPolicy(
  value: unknown,
  fallback: "veto" | "points" | "ignore" | undefined,
): "veto" | "points" | "ignore" {
  if (value === "veto" || value === "points" || value === "ignore") return value
  return fallback ?? "veto"
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}
